import { messaging, getToken, onMessage } from '../config/firebase';

// Get VAPID key from env (same logic as firebase.js)
const getVAPIDKey = () => {
  return process.env.REACT_APP_FIREBASE_VAPID_KEY;
};

/**
 * Request notification permission from the user
 * @returns {Promise<boolean>} True if permission granted, false otherwise
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return false;
  }

  const currentPermission = Notification.permission;
  console.log('[FCM] Current notification permission:', currentPermission);

  if (currentPermission === 'granted') {
    console.log('[FCM] Notification permission already granted');
    return true;
  }

  if (currentPermission === 'denied') {
    console.warn(
      '[FCM] Notification permission has been denied. User needs to enable it in browser settings.'
    );
    return false;
  }

  // Permission is 'default', request it
  try {
    console.log('[FCM] Requesting notification permission from user...');
    const permission = await Notification.requestPermission();
    console.log('[FCM] User responded with permission:', permission);
    return permission === 'granted';
  } catch (error) {
    console.error('[FCM] Error requesting notification permission:', error);
    return false;
  }
};

/**
 * Get FCM registration token
 * VAPID key is automatically read from REACT_APP_FCM_VAPID_KEY env variable
 * Firebase automatically caches the token, so we don't need to store it manually
 * @returns {Promise<string|null>} FCM token or null if unavailable
 */
export const getFCMToken = async () => {
  if (!messaging) {
    console.warn('[FCM] Firebase Messaging is not available');
    return null;
  }

  try {
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      console.warn('[FCM] Notification permission not granted');
      return null;
    }

    // Check if service worker is registered
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;

      // Automatically get VAPID key from env
      const vapidKey = getVAPIDKey();

      // Firebase automatically caches the token in IndexedDB/Service Worker
      // So we don't need to manually cache it in localStorage
      const token = await getToken(messaging, {
        vapidKey: vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        console.log('[FCM] Registration token obtained:', token);
        return token;
      } else {
        console.warn('[FCM] No registration token available. Request permission to generate one.');
        return null;
      }
    } else {
      console.warn('[FCM] Service Worker is not supported');
      return null;
    }
  } catch (error) {
    console.error('[FCM] An error occurred while retrieving token:', error);
    return null;
  }
};

/**
 * Register service worker for FCM
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker is not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });
    console.log('Service Worker registered successfully:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

/**
 * Set up foreground message handler
 * This will show notifications when the app is in the foreground
 * @param {Function} onMessageCallback - Optional callback to handle messages
 */
export const setupForegroundMessageHandler = (onMessageCallback = null) => {
  if (!messaging) {
    console.warn('Firebase Messaging is not available');
    return;
  }

  onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);

    // Don't show toast here - let saga handle it via callback
    // This prevents duplicate toasts (toast here + showToastAlert in saga)
    // Call custom callback if provided (saga will handle toast and refresh)
    if (onMessageCallback) {
      onMessageCallback(payload);
    }
  });
};

/**
 * Initialize FCM - register service worker and request permission
 * VAPID key is automatically read from REACT_APP_FCM_VAPID_KEY env variable
 * @returns {Promise<string|null>} FCM token
 */
export const initializeFCM = async () => {
  try {
    // Register service worker first
    await registerServiceWorker();

    // Get FCM token (VAPID key is automatically read from env)
    const token = await getFCMToken();
    return token;
  } catch (error) {
    console.error('Error initializing FCM:', error);
    return null;
  }
};
